import './index.less';

import {
  DisabledCircleIcon,
  RadioCheckedIcon,
  RadioUncheckedIcon,
  UncheckedCircleIcon,
} from 'components/Icons';
import RevxFlexLayout, { Props } from 'components/RevxFlexLayout';
import { useEffect, useState } from 'react';

export type RevxRadioPropsOption = {
  children?:
    | React.ReactElement
    | (React.ReactElement | null)[]
    | React.ReactText;
  otherChildren?:
    | React.ReactElement
    | (React.ReactElement | null)[]
    | React.ReactText;
  value: string | number;
  disabled?: boolean;
};

export type RevxRadioProps = {
  unCheckIconSize?: 'big' | 'small';
  value?: string | number;
  disabled?: boolean;
  onChange?: (value: string | number) => void;
  options: RevxRadioPropsOption[];
  outSideRevxLayoutProps?: Props;
  inSideRevxLayoutProps?: Props;
  iconSequence?: 'first' | 'last';
  allowClear?: boolean;
};

export const RevxRadio = ({
  value,
  outSideRevxLayoutProps,
  inSideRevxLayoutProps,
  options,
  onChange,
  iconSequence = 'first',
  unCheckIconSize = 'big',
  disabled,
  allowClear = false,
}: RevxRadioProps) => {
  const [curValue, setCurValue] = useState(value);

  useEffect(() => {
    // controlled mode
    if (value !== undefined) {
      setCurValue(value);
    }
  }, [value]);

  const formChange = (e: string | number) => {
    setCurValue(e);

    if (typeof onChange === 'function') {
      onChange(e);
    }
  };

  const onCurChange = (e: string | number) => {
    if (disabled) {
      return false;
    }
    if (curValue !== e) {
      formChange(e);
    } else {
      if (allowClear) {
        formChange('');
      }
    }
  };

  const iconRender = (item: RevxRadioPropsOption) => {
    if (iconSequence === 'first') {
      return (
        <>
          <>{item?.otherChildren}</>
          {item.value === curValue ? (
            <RadioCheckedIcon />
          ) : (
            <>
              {unCheckIconSize === 'big' ? (
                <RadioUncheckedIcon />
              ) : (
                <>
                  {item.disabled ? (
                    <DisabledCircleIcon />
                  ) : (
                    <UncheckedCircleIcon />
                  )}
                </>
              )}
            </>
          )}
          <>{item?.children}</>
        </>
      );
    } else {
      return (
        <>
          <>{item?.children}</>
          {item.value === curValue ? (
            <RadioCheckedIcon />
          ) : (
            <>
              {unCheckIconSize === 'big' ? (
                <RadioUncheckedIcon />
              ) : (
                <UncheckedCircleIcon />
              )}
            </>
          )}
          <>{item?.otherChildren}</>
        </>
      );
    }
  };

  const itemRender = (item: RevxRadioPropsOption) => {
    if (inSideRevxLayoutProps?.align && inSideRevxLayoutProps.justify) {
      return (
        <RevxFlexLayout {...inSideRevxLayoutProps}>
          {iconRender(item)}
        </RevxFlexLayout>
      );
    } else {
      return <div {...inSideRevxLayoutProps}>{iconRender(item)}</div>;
    }
  };

  return (
    <RevxFlexLayout
      {...outSideRevxLayoutProps}
      className={`revx-radio-group ${outSideRevxLayoutProps?.className}`}
    >
      {options.map((item) => (
        <div
          className={`revx-radio-group-item ${
            item?.value === curValue ? 'revx-radio-group-item-active' : ''
          } ${
            disabled || item.disabled ? 'revx-radio-group-item-disabled' : ''
          }`}
          onClick={() => !item.disabled && onCurChange(item.value)}
          key={item.value}
        >
          {itemRender(item)}
        </div>
      ))}
    </RevxFlexLayout>
  );
};
